'use client';
import React, { useEffect, useState } from 'react';
import { IEvaluation } from '@/lib/evaluationsApi';
import * as Styled from './styled';
import { IDataFrameSchema } from '@/lib/dataFrameTypes';
import { TEvaluationModel } from '@/lib/evaluationModelsApi';
import { Row, Stack } from '@/ui/primatives';
import Input from '@/components/Input';
import Button from '@/ui/Button';
import * as Icon from '@/ui/Icon';
import { PromptDisplay } from '../PromptEditor';
import TargetFieldSelect from '../TargetFieldSelect';
import { SourceFileInfo } from '../EvaluationFileInfo';
import ModelSelect from '../ModelSelect';
import Flash from '@/ui/Flash';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import { getTypeOptions } from '../lib';
import JsonView from '@/components/JsonView';

import TargetStatusFlash from './TargetStatusFlash';

const formSchema = Yup.object().shape({
  name: Yup.string(),
  model_id: Yup.string().required('Required'),
  prompt: Yup.string().required('Required'),
  type: Yup.string().required('Required'),
  // sample_count: Yup.number(),
  target_column: Yup.string().required('Required'),
  target_branch: Yup.string().required('Required'),
  target_path: Yup.string().required('Required'),
  auto_commit: Yup.boolean(),
  commit_message: Yup.string(),
  target_type: Yup.string().required('Required'),
});

const RunForm: React.FC<{
  evaluation: IEvaluation;
  availableModels: TEvaluationModel[];
  schema: IDataFrameSchema;
  children: React.ReactNode;
  onRun: (data: any) => void; // TODO: Maybe create IEvaluationCreateParams interface
  onBack: () => void;
}> = ({ evaluation, availableModels, schema, children, onRun, onBack }) => {
  const {
    handleSubmit,
    register,
    reset,
    watch,
    control,
    formState: { errors },
    ...rest
  } = useForm({
    resolver: yupResolver(formSchema as any),
    defaultValues: {
      ...evaluation,
      type: `${evaluation.input_type}|${evaluation.output_type}`,
      model_id: evaluation.model_id || evaluation.model.id,
      name: evaluation.name || undefined,
      target_type: evaluation.target_type || 'commit',
    },
  });
  const [targetType, setTargetType] = useState(watch('target_type'));

  // console.log('evaluation', evaluation);

  // const autoCommitWatch = watch('auto_commit');
  const targetBranchWatch = watch('target_branch');
  const targetPathWatch = watch('target_path');
  const targetTypeWatch = watch('target_type');

  useEffect(() => {
    setTargetType(targetTypeWatch);
  }, [targetTypeWatch]);

  // const [targetType, setTargetType] = useState(targetTypeWatch);

  const onReturnToSetup = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    onBack();
  };

  const typeOptions = getTypeOptions(availableModels);

  const outputTypeOptions = [
    { label: 'Commit results on completion', value: 'commit' },
    { label: 'Save results for review before committing', value: 'workspace' },
  ];

  // HACK to force auto_commit to reflect the selected target_type
  const sanitizeSubmittitedEvaluation = (data: any) => {
    const autoCommit = data.target_type === 'commit';
    const submissionEvaluation = {
      ...data,
      auto_commit: autoCommit,
    };
    delete submissionEvaluation.target_type;
    return submissionEvaluation;
  };

  const onSubmit = (data: any) => {
    onRun(sanitizeSubmittitedEvaluation(data));
  };

  return (
    <Styled.RunForm {...rest}>
      <Styled.SplitFormWrapper>
        <Stack gap={10} style={{ position: 'relative' }} data-tutorialid="evaluation-wizard-setup-config">
          <SourceFileInfo evaluation={evaluation} />
          <Input.Select
            name="type"
            disabled
            label="Evaluation Type"
            error={errors?.type}
            control={control}
            options={typeOptions}
            wide
          />
          <ModelSelect
            disabled
            name="model_id"
            label="Model"
            options={evaluation.model ? [evaluation.model] : availableModels}
            control={control}
            error={errors?.model_id}
          />
          {evaluation.output_type === 'embeddings' ? (
            <Input name="prompt" label="Embedding Field" disabled value={evaluation.prompt} wide />
          ) : (
            <Stack gap={5} style={{ flex: 1 }}>
              <label>Prompt</label>
              <PromptDisplay prompt={evaluation.prompt} />
            </Stack>
          )}
          <TargetFieldSelect
            label="Output field"
            name="target_column"
            hint={null}
            wide
            disabled
            error={errors?.target_column}
            schema={schema}
            control={control}
          />
        </Stack>

        {/* <JsonView json={evaluation} /> */}
        <Stack gap={20} data-tutorialid="evaluation-wizard-setup-target">
          <Input.RadioButtonGroup
            name="target_type"
            // label="Output target"
            noLabel
            options={outputTypeOptions}
            control={control}
            submit={register}
            error={errors?.target_type}
            wide
          />

          <Row alignItems="center" gap={20}>
            <Input
              disabled={targetType === 'workspace'}
              name="target_branch"
              label="Target Branch" // TODO: make this a branch picker
              submit={register}
              error={errors?.target_branch}
              hint="Branch to save the result file, if it does not exist it will be created."
              style={{ flex: 1, width: '360px' }}
            />

            <Input
              disabled={targetType === 'workspace'}
              name="target_path"
              label="Target Path"
              submit={register}
              error={errors?.target_path}
              hint="Where to save the result file, if it does not exist it will be created."
              style={{ flex: 1, width: '360px' }}
            />
          </Row>

          <TargetStatusFlash
            evaluation={evaluation}
            targetType={targetType}
            targetPath={targetPathWatch}
            targetBranch={targetBranchWatch}
          />

          <Stack gap={20} justifyContent="flex-end" style={{ flex: 1 }}>
            {targetType === 'commit' && (
              <Input.Textarea
                name="commit_message"
                wide
                label="Commit Message"
                submit={register}
                error={errors?.commit_message}
                // rows={3}
              />
            )}

            <Row alignItems="flex-end" justifyContent="flex-end">
              <Row alignItems="center" gap={10}>
                <Button.Basic onClick={onReturnToSetup} small>
                  <Icon.ArrowLeft /> Back
                </Button.Basic>
                <Button.Primary small onClick={handleSubmit(onSubmit)}>
                  Run Evaluation
                </Button.Primary>
              </Row>
            </Row>
          </Stack>
        </Stack>
        {children}
      </Styled.SplitFormWrapper>
      {/* <DevTool control={control} /> */}
    </Styled.RunForm>
  );
};

export default RunForm;
